import { useMemo, useRef, useState } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Divider, Modal, Typography } from '@kc-console/common-ui';
import { useGetAnnouncementDetail, useGetAnnouncementList } from '@kc-console/context';
import { useTranslation } from '@kc-console/core';

import AnnouncementArticleList from './AnnouncementArticleList';
import AnnouncementContentArea from './AnnouncementContentArea';
import { createAnnouncementBadgeColorMap } from './utils';

import type { ModalComponentProps } from '@kc-console/context';

/** 모달 최대 높이(px) */
const MODAL_MAX_HEIGHT = 920;

/** 공통 Modal의 lg 너비(1024px)에서 좌우 패딩(각 32px)을 제외한 콘텐츠 너비를 960px로 유지하기 위한 최소 너비(px) */
const ANNOUNCEMENT_MODAL_MIN_WIDTH = 1024;

/**
 * 공통 Modal은 화면이 좁아지면 Paper가 반응형으로 축소됩니다.
 * 새소식 본문의 외부 HTML 스타일이 모달 너비에 영향을 주지 않도록 hosts/layout에서만 Paper 최소 너비를 고정합니다.
 */
const ANNOUNCEMENT_MODAL_STYLE = {
  '& .MuiDialog-paper': {
    minWidth: ANNOUNCEMENT_MODAL_MIN_WIDTH,
  },
} as const;

/** 모달 헤더 높이(px): 상하 패딩(32 + 24) + 닫기 버튼(높이 48 − 음수 마진 14 = 34, 제목 30보다 큼) */
const MODAL_HEADER_HEIGHT = 90;

/** footer 제거로 사라진 모달 하단 여백(px) */
const SCROLL_AREA_BOTTOM_SPACING = 32;

/** 본문·구분선·목록을 함께 담는 스크롤 영역 높이(px). 모달 최대 높이에서 헤더와 하단 여백을 뺀 나머지입니다. */
const SCROLL_AREA_HEIGHT = MODAL_MAX_HEIGHT - MODAL_HEADER_HEIGHT - SCROLL_AREA_BOTTOM_SPACING;

export interface AnnouncementDetailModalProps extends ModalComponentProps {
  /** 위젯에서 선택된 새소식 ID (초기 선택 글 및 초기 페이지 계산 기준) */
  initialAnnouncementId: string;
}

/**
 * 카카오클라우드 새소식 상세 모달입니다.
 * @description
 * - 본문은 상세 API로, 하단 목록은 목록 API 전체 조회(limit -1)로 각각 조회하며
 *   목록 페이지네이션은 클라이언트에서 처리합니다.
 * - 대시보드는 위젯이 목록을 미리 적재하지만 GNB 알림에서 열면 이 모달이 처음 조회하므로,
 *   목록 적재 전에도 로딩 오버레이를 유지해 빈 목록이 스치지 않게 합니다.
 * - 선택 글(selectedId)만 이 컴포넌트가 소유하고, 목록 페이지 상태는 하단 목록 컴포넌트가 소유합니다.
 *   덕분에 페이지 이동(이전/다음)은 목록만 리렌더하고 상단 본문 영역은 영향을 받지 않습니다.
 */
const AnnouncementDetailModal = (props: AnnouncementDetailModalProps) => {
  const { onClose, TransitionProps, open = false } = props;
  const { initialAnnouncementId } = props;
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState(initialAnnouncementId);
  const {
    data: listData,
    isError: isListError,
    isLoading: isListLoading,
  } = useGetAnnouncementList(undefined, {
    refetchOnMount: false,
  });

  const {
    data: selectedAnnouncement,
    isError: isDetailError,
    isFetching: isDetailFetching,
  } = useGetAnnouncementDetail(selectedId);

  const announcements = useMemo(() => listData?.data ?? [], [listData?.data]);
  const badgeColorMap = useMemo(
    () => createAnnouncementBadgeColorMap(announcements),
    [announcements],
  );
  // 목록 적재 후에만 마운트하기 위한 가드 — 빈 배열로 먼저 마운트되면 초기 페이지가 0 에 고정됩니다.
  const hasList = announcements.length > 0;
  const isError = isListError || isDetailError;
  const isLoading = isListLoading || isDetailFetching;
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const handleSelectAnnouncement = (id: string) => {
    setSelectedId(id);
    scrollAreaRef.current?.scrollTo({ top: 0 });
  };

  const body = selectedAnnouncement ? (
    <ScrollArea ref={scrollAreaRef}>
      <AnnouncementContentArea
        announcement={selectedAnnouncement}
        badgeColor={badgeColorMap.get(selectedAnnouncement.subTitle)}
      />
      <Divider />
      {hasList && (
        <AnnouncementArticleList
          announcements={announcements}
          initialAnnouncementId={initialAnnouncementId}
          selectedId={selectedId}
          onSelect={handleSelectAnnouncement}
        />
      )}
    </ScrollArea>
  ) : !isLoading ? (
    <Typography variant="body/normal/md" color="text/weakest/default">
      {isError
        ? t('layout:announcement.detail.err_msg.default')
        : t('layout:announcement.detail.empty_msg')}
    </Typography>
  ) : (
    <BodyPlaceholder />
  );

  return (
    <Modal
      open={open}
      size="lg"
      sx={ANNOUNCEMENT_MODAL_STYLE}
      loading={isLoading}
      onClose={() => !isLoading && onClose()}
      slotProps={{
        transition: TransitionProps,
        header: { title: '카카오클라우드 새소식' },
        body: { children: body },
      }}
    />
  );
};

export default AnnouncementDetailModal;

/** 로딩 중 본문 자리를 유지하는 빈 영역입니다. 스크롤 영역과 같은 크기를 차지해 모달이 줄어들지 않습니다. */
const BodyPlaceholder = styled('div')`
  width: 100%;
  height: ${SCROLL_AREA_HEIGHT}px;
  margin-bottom: ${SCROLL_AREA_BOTTOM_SPACING}px;
`;

const ScrollArea = styled('div')(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 32px;
    height: ${SCROLL_AREA_HEIGHT}px;
    margin-bottom: ${SCROLL_AREA_BOTTOM_SPACING}px;
    overflow-y: auto;

    /* 본문이 길어질 때 자식이 눌리는 대신 스크롤되도록 합니다. */
    > * {
      flex-shrink: 0;
    }

    &::-webkit-scrollbar {
      width: 14px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${theme.color['interactive/default']};
      border-radius: 999px;
      border: 4px solid ${theme.color['surface/default']};
    }
  `,
);
